import React from 'react';
import {
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  TextInput,
  ReferenceArrayInput,
  BooleanInput,
  useGetIdentity,
  required,
  ImageInput,
  ImageField,
  useRecordContext,
  AutocompleteArrayInput,
  maxLength,
} from 'react-admin';
import usePlacesAutocomplete, {
  getDetails,
  getGeocode,
  getLatLng,
  Suggestion,
} from 'use-places-autocomplete';
import { useController } from 'react-hook-form';
import { EventSingleDateFields } from './EventSingleDateFields';
import { EventRepeatedDateFields } from './EventRepeatedDateFields';

export type EventFieldsProps = {
  viewType?: 'create' | 'edit';
};

export const EventFields = ({ viewType = 'create' }: EventFieldsProps) => {
  const { identity } = useGetIdentity();
  const record = useRecordContext();

  const addressInput = useController({ name: 'address' });
  const latInput = useController({ name: 'lat' });
  const lngInput = useController({ name: 'lon' });
  const countryIdInput = useController({ name: 'countryId' });

  const onlineInput = useController({ name: 'online' });

  const organizerInput = useController({
    name: 'organizer',
  });
  const iAmOrganizerInput = useController({ name: 'creatorIsOrganizer' });

  const categoriesSelectInput = useController({
    name: 'categoryIds',
    defaultValue:
      record?.categories?.map((item: { id: string }) => item.id) ?? [],
  });

  const repeatedInput = useController({ name: 'isRepeated' });

  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete();

  const handleInputAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    addressInput.field.onChange(e.target.value);
    setValue(e.target.value);
  };

  const handleSelectAddress =
    ({ description, place_id }: Suggestion) =>
    () => {
      setValue(description, false);
      addressInput.field.onChange(description);
      clearSuggestions();

      getGeocode({ address: description }).then((results) => {
        const { lat, lng } = getLatLng(results[0]);
        latInput.field.onChange(lat);
        lngInput.field.onChange(lng);
      });

      getDetails({ placeId: place_id, fields: ['address_components'] }).then(
        (details: any) => {
          const countryCode = details?.address_components.find((item: any) =>
            item.types.includes('country'),
          )?.short_name;

          if (countryCode) {
            countryIdInput.field.onChange(countryCode);
          }
        },
      );
    };

  const renderSuggestionsAddress = () =>
    data.map((suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
      } = suggestion;

      return (
        <ListItem
          key={place_id}
          disablePadding
          onClick={handleSelectAddress(suggestion)}
        >
          <ListItemButton>
            <ListItemText primary={`${main_text} ${secondary_text}`} />
          </ListItemButton>
        </ListItem>
      );
    });

  const isOnlineEvent = onlineInput.field.value === true;
  const isRepeatedEvent = repeatedInput.field.value === true;

  return (
    <>
      <TextInput
        source="name"
        label="Event Name"
        fullWidth
        validate={[required()]}
      />
      <TextInput
        source="description"
        label="Description"
        fullWidth
        multiline
        resettable
        validate={[required(), maxLength(1000)]}
        helperText="Must be 1000 characters or less"
      />

      <BooleanInput
        {...onlineInput.field}
        source="online"
        label="Online event"
        defaultValue={false}
      />

      {isOnlineEvent ? null : (
        <>
          <TextInput
            {...addressInput.field}
            source="address"
            label="Address"
            onChange={handleInputAddress}
            disabled={!ready}
            fullWidth
          />
          {status === 'OK' && <List>{renderSuggestionsAddress()}</List>}

          <Grid container columnSpacing={2}>
            <Grid item xs={3}>
              <TextInput
                {...countryIdInput.field}
                source="countryId"
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <TextInput
                {...latInput.field}
                source="lat"
                label="Coordinate lat"
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <TextInput
                {...lngInput.field}
                source="lon"
                label="Coordinate lng"
                disabled
                fullWidth
              />
            </Grid>
          </Grid>
        </>
      )}

      {viewType === 'create' ? (
        <BooleanInput
          {...repeatedInput.field}
          source="isRepeated"
          label="Repeated event"
          defaultValue={false}
        />
      ) : null}

      {isRepeatedEvent ? (
        <EventRepeatedDateFields />
      ) : (
        <EventSingleDateFields />
      )}

      <ReferenceArrayInput
        source="categories"
        label="Categories"
        reference="/categories"
      >
        <AutocompleteArrayInput
          {...categoriesSelectInput.field}
          optionText="name"
          fullWidth
        />
      </ReferenceArrayInput>

      <BooleanInput
        {...iAmOrganizerInput.field}
        onChange={(e) => {
          iAmOrganizerInput.field.onChange(e.target.checked);

          if (e.target.checked && identity?.fullName) {
            organizerInput.field.onChange(identity.fullName);
          } else {
            organizerInput.field.onChange('');
          }
        }}
        source="creatorIsOrganizer"
        label="I am Organizer"
        defaultValue={false}
      />
      <TextInput
        {...organizerInput.field}
        source="organizer"
        label="Organizer"
        fullWidth
      />

      {viewType === 'create' ? (
        <>
          <ImageInput source="picture" label="Picture">
            <ImageField source="src" title="title" />
          </ImageInput>
          <TextInput source="pictureLink" label="Image" fullWidth />
        </>
      ) : null}

      {/* @whitish попросив вимкнути ці поля поки що */}
      {/* <NumberInput source="capacity" label="Capacity" />
      <NumberInput source="cost" label="Cost" /> */}
    </>
  );
};
